import type { Alpha3Code } from 'i18n-iso-countries'

import type { Money } from '@percent/utility'
import {
  OpportunityType,
  type OpportunityLocationDto,
  OpportunityLocationTypeDto,
  type SurveyConfig,
  type SurveyConfigResponse
} from '@percent/domain/volunteering'

export { OpportunityType, OpportunityLocationDto, OpportunityLocationTypeDto, SurveyConfig, SurveyConfigResponse }

export enum OpportunityStatus {
  Draft = 'draft',
  Published = 'published',
  Closed = 'closed',
  Cancelled = 'cancelled'
}

export enum OpportunityStatusDto {
  Active = 'active',
  Inactive = 'inactive'
}

export enum OpportunityTimeTracking {
  Automatic = 'automatic',
  Manual = 'manual'
}

export enum ExternalParticipationStatus {
  AwaitingApproval = 'awaiting_approval',
  Registered = 'registered',
  Rejected = 'rejected',
  Confirmed = 'confirmed',
  NotParticipating = 'not_participating',
  Waitlisted = 'waitlisted'
}

export type OpportunitySkillDto = Readonly<{
  id: string
  name: string
}>

export type OpportunityActivityDto = OpportunitySkillDto

export type OpportunityExternalDataDto = Readonly<{
  provider: string
}>

export type OpportunityItineraryDto = Readonly<{
  id: number
  durationMinutes: number
  title: string
  description?: string | null
}>

export type OpportunityDto = Readonly<{
  id: string
  name: string
  description: string | null
  parentId: string | null
  image: string | null
  startDate: string | null
  endDate: string | null
  location: OpportunityLocationDto
  type: OpportunityType
  organiserId: string | null
  skills: OpportunitySkillDto[]
  activities: OpportunityActivityDto[]
  spots: number | null
  itinerary: OpportunityItineraryDto | null
  external: OpportunityExternalDataDto | null
  timeTracking: OpportunityTimeTracking
  targetCountries: string[] | null
}>

export type OpportunityDetailDto = Readonly<{
  id: string
  name: string
  description: string | null
  parentId: string | null
  image: string | null
  type: OpportunityType
  status: OpportunityStatus
  location: OpportunityLocationDto
  startDate: string | null
  endDate: string | null
  organisation: {
    id: string
    name: string
    logo: string | null
    countryCode: string
  } | null
  organiser: {
    id: string
    fullName: string
    preferredName?: string | null
    image: string | null
    email: string
  } | null
  skills: {
    id: string
    name: string
  }[]
  spots: number | null
  participantsCount: number
  activities: {
    id: string
    name: string
  }[]
  itinerary:
    | {
        id: number
        durationMinutes: number
        title: string
        description: string | null
      }[]
    | null
  participation?: {
    status: 'participating' | 'not_participating' | 'awaiting_approval' | 'waitlisted'
  } | null
  timeTracking: OpportunityTimeTracking
  external: {
    provider: string
  } | null
  managedByOrganisation?: boolean
  cancelledBy: string | null
  notificationsEnabled: boolean
  waitlistEnabled: boolean
  timeLogsAreRewardsEligible: boolean
  participantCounts?: {
    registered: number
    confirmed: number
    waitlisted: number
  }
  targetCountries: Alpha3Code[]
  survey?: SurveyConfigResponse
  visibility?: 'listed' | 'unlisted'
  /** IANA timezone ID. Null for legacy records — consumers should fall back to browser timezone. */
  timeZone?: string | null
}>

export type OpportunitySelfParticipationDto = Readonly<{
  participationStatus: ExternalParticipationStatus
  registrationDate: string | null
  opportunity: {
    id: string
    name: string
    type: OpportunityType
    status: 'active' | 'closed' | 'draft'
    parent: { id: string; name: string } | null
    location: OpportunityLocationDto
    organisation: {
      id: string
      name: string
      logo?: string
    } | null
    organiser: {
      id: string
      fullName: string
      preferredName?: string | null
      image: string | null
      email: string
    } | null
    startDate: string
    endDate?: string
    spots?: number
    participantsCount: number
    activities: { id: string; name: string }[]
    isCreatedByMe: boolean
    timeTracking: OpportunityTimeTracking
    external?: { provider: string }
    image: string | null
    surveyIds: string[]
  }
}>

export enum VolunteeringTimeLogStatus {
  AwaitingApproval = 'awaiting_approval',
  Approved = 'approved',
  Deleted = 'deleted'
}

export enum VolunteeringTimeLogType {
  OnPlatform = 'on_platform',
  OffPlatform = 'off_platform'
}

export type TimeLogDetailDto = Readonly<{
  id: string
  startTime: string
  endTime: string
  status: VolunteeringTimeLogStatus
  type: VolunteeringTimeLogType
  activities: { id: string; name: string }[]
  skills: { id: string; name: string }[]
  opportunity: {
    id: string
    name: string
    type: OpportunityType
  } | null
  organisation: {
    id: string
    name: string
    logo: string | null
  } | null
  duringWorkingHours?: boolean
  loggedAt?: string
  createdAt: string
  updatedAt: string
  rewards?: VolunteeringTimeLogReward[]
}>

export type VolunteeringTimeLogReward = Readonly<{
  accountRewardId: string
  organisationId: string
  contributedAmount: Money
  rewardAmount: Money
}>

export type ChildSessionEvent = Readonly<{
  id: string
  name: string
  startDate: Date
  endDate: Date
  location: OpportunityLocationDto
  participantSpots?: number
  targetCountries: string[] | null
  waitlistEnabled?: boolean
  timeZone?: string | null
}>
